<script setup>
// Auto-extracted chrome for PvCheckPro.vue — header + footer copied verbatim
// from Home so legal pages (Impressum/Datenschutz/AGB) render with the
// pilot's own masthead + footer (no AppLayout chrome leak).
// Body markup of the wrapper is yielded via <slot />.
import { computed } from 'vue';
import { Head } from '@inertiajs/vue3';

const props = defineProps({
    domain: { type: Object, default: () => ({}) },
    brandName: { type: String, default: "PV Check Pro" },
    brandTagline: { type: String, default: "Unabhängige Zweitmeinung zu Ihrem PV-Angebot" },
    brandAccent: { type: String, default: '#0EA5E9' },
    author: { type: Object, default: null },
    designVersion: { type: String, default: '' },
});

// Derive a few values that the chrome HTML references. Best-effort fallbacks
// for the values the chrome's masthead/footer reads.
const today = computed(() => new Date().toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }));
const brandName = computed(() => props.brandName || props.domain?.settings?.brand_name || "PV Check Pro");
const brandTagline = computed(() => props.brandTagline || props.domain?.settings?.brand_tagline || "Unabhängige Zweitmeinung zu Ihrem PV-Angebot");
const brandAccent = computed(() => props.brandAccent);
const author = computed(() => props.author);
const DESIGN_VERSION = computed(() => props.designVersion || '');


const fontsHref = "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Inter:wght@400;500;600&display=swap";
</script>

<template>
    <div class="pvc" :style="`--pvc-accent:${brandAccent};`">
        <Head>
            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
            <link rel="stylesheet" :href="fontsHref" />
        </Head>
        <header class="pvc-bar">
                    <div class="pvc-bar__inner">
                        <a class="pvc-logo" href="/" aria-label="Startseite">
                            <span class="pvc-logo__seal" aria-hidden="true">
                                <span class="pvc-logo__check"></span>
                            </span>
                            <span class="pvc-logo__txt">
                                <span class="pvc-logo__brand">{{ brandName }}</span>
                                <span class="pvc-logo__tag">{{ brandTagline }}</span>
                            </span>
                        </a>
                        <nav class="pvc-bar__nav" aria-label="Hauptnavigation">
                            <a href="#tool">Angebots-Check</a>
                            <a href="#methodik">Methodik</a>
                            <a href="#faq">Fragen</a>
                            <a href="/">Übersicht</a>
                            <a href="/ratgeber">Ratgeber</a>
                        </nav>
                    </div>
                </header>
        <slot />
        <footer class="pvc-foot">
                    <div class="pvc-foot__inner">
                        <p>&copy; {{ new Date().getFullYear() }} {{ brandName }} &middot; {{ brandTagline }}</p>
                        <nav>
                            <a href="/impressum">Impressum</a>
                            <a href="/datenschutz">Datenschutz</a>
                            <a href="/agb">AGB</a>
                            <a href="/widerruf">Widerruf</a>
                            <a href="/ratgeber">Ratgeber</a>
                        </nav>
                    </div>
                </footer>
    </div>
</template>

<style scoped>

/* PvCheckPro pilot 2026-05. pvc-* namespace. Unique vs. wpa-* / pvk-* / sht-*:
   Space Grotesk display + Inter body, green accent #15803D, off-white page,
   numbered tag-chip rail, paste-form card with explicit progress rail, big
   "verdict-card" result block with horizontal bench KPIs and dual-color
   findings boxes (red flags / green positives), sticky mobile CTA pill. */

.pvc {
    --pvc-ink:       #0c1410;
    --pvc-ink-2:     #2d3a33;
    --pvc-mute:      #6b7a72;
    --pvc-page:      #f5f7f4;
    --pvc-surface:   #ffffff;
    --pvc-line:      #d8dfd8;
    --pvc-line-2:    #b9c4bb;
    --pvc-accent-soft: rgba(21, 128, 61, 0.10);
    --pvc-red:       #b91c1c;
    --pvc-red-soft:  #fef2f2;
    --pvc-green-soft: #ecfdf5;

    background: var(--pvc-page);
    color: var(--pvc-ink);
    font-family: 'Inter', system-ui, -apple-system, Segoe UI, sans-serif;
    font-size: 16px;
    line-height: 1.55;
    -webkit-font-smoothing: antialiased;
    min-height: 100dvh;
}
.pvc * { box-sizing: border-box; }
.pvc h1, .pvc h2, .pvc h3 { font-family: 'Space Grotesk', system-ui, sans-serif; font-weight: 600; letter-spacing: -0.02em; }
.pvc a { color: inherit; }

/* ── Top bar ─────────────────────────────────────────────────────────── */
.pvc-bar { border-bottom: 1px solid var(--pvc-line); background: var(--pvc-page); position: sticky; top: 0; z-index: 30; }
.pvc-bar__inner { max-width: 1200px; margin: 0 auto; padding: 16px 28px; display: flex; align-items: center; justify-content: space-between; gap: 24px; }
.pvc-logo { display: inline-flex; align-items: center; gap: 12px; text-decoration: none; }
.pvc-logo__seal { width: 36px; height: 36px; border-radius: 8px; background: var(--pvc-accent); display: inline-flex; align-items: center; justify-content: center; box-shadow: 0 2px 6px rgba(21,128,61,0.22); }
.pvc-logo__check { width: 16px; height: 8px; border-left: 2px solid #fff; border-bottom: 2px solid #fff; transform: rotate(-45deg) translate(2px, -3px); }
.pvc-logo__txt { display: flex; flex-direction: column; line-height: 1.1; }
.pvc-logo__brand { font-family: 'Space Grotesk', sans-serif; font-weight: 600; font-size: 18px; color: var(--pvc-ink); letter-spacing: -0.01em; }
.pvc-logo__tag { font-size: 11px; color: var(--pvc-mute); margin-top: 3px; }
.pvc-bar__nav { display: flex; gap: 24px; }
.pvc-bar__nav a { text-decoration: none; font-size: 13px; color: var(--pvc-ink-2); transition: color 180ms; }
.pvc-bar__nav a:hover { color: var(--pvc-accent); }
@media (max-width: 700px) { .pvc-bar__nav { display: none; } }

/* ── Hero ────────────────────────────────────────────────────────────── */
.pvc-hero { max-width: 1200px; margin: 0 auto; padding: 80px 28px 64px; display: grid; grid-template-columns: minmax(0, 1.6fr) minmax(0, 1fr); gap: 56px; align-items: start; }
@media (max-width: 920px) { .pvc-hero { padding: 56px 24px 40px; grid-template-columns: 1fr; gap: 36px; } }

.pvc-tagchip { display: inline-block; background: var(--pvc-accent-soft); color: var(--pvc-accent); font-size: 12px; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; padding: 8px 14px; border-radius: 999px; margin: 0 0 22px; }
.pvc-hero__h1 { font-size: clamp(38px, 5.8vw, 68px); line-height: 1.02; margin: 0 0 22px; color: var(--pvc-ink); }
.pvc-hero__h1-accent { color: var(--pvc-accent); }
.pvc-hero__lede { font-size: 17px; line-height: 1.6; color: var(--pvc-ink-2); max-width: 56ch; margin: 0 0 32px; }

.pvc-trust { list-style: none; padding: 0; margin: 0 0 36px; display: grid; gap: 14px; }
.pvc-trust li { display: grid; grid-template-columns: 38px 1fr; gap: 14px; align-items: baseline; font-size: 14px; color: var(--pvc-ink-2); }
.pvc-trust__no { font-family: 'Space Grotesk', sans-serif; font-weight: 600; font-size: 12px; color: var(--pvc-accent); letter-spacing: 0.06em; padding-top: 1px; }

.pvc-hero__ctas { display: flex; gap: 14px; flex-wrap: wrap; }

.pvc-hero__panel { background: var(--pvc-surface); border: 1px solid var(--pvc-line); border-radius: 12px; padding: 28px; box-shadow: 0 1px 0 rgba(12,20,16,0.04); }
.pvc-panel__head { font-family: 'Space Grotesk', sans-serif; font-weight: 600; font-size: 13px; letter-spacing: 0.08em; text-transform: uppercase; color: var(--pvc-accent); margin: 0 0 18px; }
.pvc-pricetable { width: 100%; border-collapse: collapse; }
.pvc-pricetable th, .pvc-pricetable td { text-align: left; padding: 10px 0; border-bottom: 1px solid var(--pvc-line); font-size: 13px; }
.pvc-pricetable th { font-family: 'Space Grotesk', sans-serif; font-weight: 500; color: var(--pvc-mute); font-size: 11px; letter-spacing: 0.06em; text-transform: uppercase; }
.pvc-pricetable td { color: var(--pvc-ink); font-variant-numeric: tabular-nums; }
.pvc-pricetable td + td { text-align: right; font-weight: 500; }
.pvc-pricetable tr:last-child td { border-bottom: 0; }
.pvc-panel__foot { font-size: 11px; color: var(--pvc-mute); margin: 14px 0 0; line-height: 1.5; }

/* ── CTA buttons ─────────────────────────────────────────────────────── */
.pvc-cta { display: inline-flex; align-items: center; justify-content: center; gap: 6px; font-family: 'Space Grotesk', sans-serif; font-weight: 600; font-size: 14px; padding: 14px 22px; border-radius: 8px; cursor: pointer; text-decoration: none; border: 1px solid transparent; transition: background 180ms, color 180ms, border-color 180ms, transform 180ms; }
.pvc-cta--primary { background: var(--pvc-accent); color: #fff; border-color: var(--pvc-accent); }
.pvc-cta--primary:hover:not(:disabled) { background: #126a32; border-color: #126a32; }
.pvc-cta--ghost { background: transparent; color: var(--pvc-ink); border-color: var(--pvc-line-2); }
.pvc-cta--ghost:hover { border-color: var(--pvc-ink); }
.pvc-cta--lg { padding: 16px 28px; font-size: 15px; }
.pvc-cta:disabled { opacity: 0.45; cursor: not-allowed; }

/* ── Tool ────────────────────────────────────────────────────────────── */
.pvc-tool { background: var(--pvc-surface); border-top: 1px solid var(--pvc-line); border-bottom: 1px solid var(--pvc-line); padding: 72px 0; }
.pvc-tool__inner { max-width: 880px; margin: 0 auto; padding: 0 28px; }
.pvc-tool__head { text-align: center; margin: 0 0 28px; }
.pvc-eyebrow { font-family: 'Space Grotesk', sans-serif; font-size: 12px; letter-spacing: 0.14em; text-transform: uppercase; color: var(--pvc-accent); font-weight: 600; margin: 0 0 12px; }
.pvc-h2 { font-size: clamp(28px, 4vw, 44px); margin: 0; color: var(--pvc-ink); line-height: 1.1; }

.pvc-rail { list-style: none; padding: 0; margin: 0 0 28px; display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; }
.pvc-rail li { display: flex; align-items: center; gap: 10px; padding: 12px 14px; background: var(--pvc-page); border: 1px solid var(--pvc-line); border-radius: 8px; font-size: 13px; color: var(--pvc-mute); transition: all 180ms; }
.pvc-rail--on { background: var(--pvc-accent); color: #fff; border-color: var(--pvc-accent); }
.pvc-rail--done { border-color: var(--pvc-accent); color: var(--pvc-accent); background: var(--pvc-accent-soft); }
.pvc-rail__no { font-family: 'Space Grotesk', sans-serif; font-weight: 600; font-size: 12px; letter-spacing: 0.06em; }
.pvc-rail__lab { font-weight: 500; }
@media (max-width: 620px) { .pvc-rail__lab { display: none; } .pvc-rail { grid-template-columns: repeat(4, 1fr); gap: 6px; } .pvc-rail li { justify-content: center; padding: 10px; } }

.pvc-card { background: var(--pvc-surface); border: 1px solid var(--pvc-line); border-radius: 14px; padding: 36px; box-shadow: 0 2px 8px rgba(12,20,16,0.04); }
@media (max-width: 600px) { .pvc-card { padding: 24px; } }

.pvc-pane { display: grid; gap: 26px; }
.pvc-field { display: grid; gap: 8px; }
.pvc-field label { font-family: 'Space Grotesk', sans-serif; font-size: 14px; font-weight: 500; color: var(--pvc-ink); }
.pvc-field__hint { font-size: 12px; color: var(--pvc-mute); margin: 4px 0 0; line-height: 1.5; }

.pvc-input { display: flex; align-items: center; background: var(--pvc-page); border: 1px solid var(--pvc-line); border-radius: 8px; padding: 0 16px; transition: border-color 180ms, background 180ms; }
.pvc-input:focus-within { border-color: var(--pvc-accent); background: #fff; box-shadow: 0 0 0 3px var(--pvc-accent-soft); }
.pvc-input input { font-family: 'Space Grotesk', sans-serif; font-size: 22px; font-weight: 500; flex: 1; border: 0; outline: 0; background: transparent; padding: 14px 0; color: var(--pvc-ink); }
.pvc-input__unit { font-size: 14px; color: var(--pvc-mute); font-weight: 500; padding-left: 10px; }

.pvc-info { background: var(--pvc-accent-soft); border-left: 3px solid var(--pvc-accent); border-radius: 0 8px 8px 0; padding: 18px 20px; }
.pvc-info__h { font-family: 'Space Grotesk', sans-serif; font-weight: 600; font-size: 13px; color: var(--pvc-accent); margin: 0 0 8px; letter-spacing: 0.04em; text-transform: uppercase; }
.pvc-info p { margin: 0; font-size: 13px; color: var(--pvc-ink-2); line-height: 1.6; }

.pvc-note { font-size: 14px; color: var(--pvc-ink-2); background: var(--pvc-page); border: 1px dashed var(--pvc-line-2); padding: 18px; border-radius: 8px; margin: 0; }

.pvc-card__foot { display: flex; justify-content: space-between; align-items: center; gap: 16px; padding-top: 28px; margin-top: 28px; border-top: 1px solid var(--pvc-line); }
.pvc-textlink { background: transparent; border: 0; color: var(--pvc-mute); font-family: inherit; font-size: 14px; cursor: pointer; padding: 8px 0; transition: color 180ms; }
.pvc-textlink:hover { color: var(--pvc-accent); }

.pvc-err { color: var(--pvc-red); font-size: 13px; margin: 12px 0 0; }

/* ── Result ──────────────────────────────────────────────────────────── */
.pvc-result { padding: 72px 0; background: var(--pvc-ink); color: #f5f7f4; }
.pvc-result--ok   { background: linear-gradient(180deg, #0e2d1d 0%, #0c1410 70%); }
.pvc-result--warn { background: linear-gradient(180deg, #2a2710 0%, #0c1410 70%); }
.pvc-result--bad  { background: linear-gradient(180deg, #2a1010 0%, #0c1410 70%); }
.pvc-result__inner { max-width: 1080px; margin: 0 auto; padding: 0 28px; }

.pvc-score { display: grid; grid-template-columns: auto 1fr; gap: 36px; align-items: center; padding-bottom: 32px; border-bottom: 1px solid rgba(245,247,244,0.12); }
@media (max-width: 700px) { .pvc-score { grid-template-columns: 1fr; gap: 18px; } }
.pvc-score__bigwrap { display: flex; align-items: baseline; gap: 6px; }
.pvc-score__big { font-family: 'Space Grotesk', sans-serif; font-weight: 700; font-size: clamp(96px, 14vw, 168px); line-height: 0.85; letter-spacing: -0.05em; color: #fff; }
.pvc-result--ok .pvc-score__big   { color: #4ade80; }
.pvc-result--warn .pvc-score__big { color: #facc15; }
.pvc-result--bad .pvc-score__big  { color: #f87171; }
.pvc-score__slash { font-family: 'Space Grotesk', sans-serif; font-size: 36px; color: rgba(245,247,244,0.4); }
.pvc-score__max { font-family: 'Space Grotesk', sans-serif; font-size: 22px; color: rgba(245,247,244,0.55); }
.pvc-score__eyebrow { font-size: 11px; letter-spacing: 0.14em; text-transform: uppercase; color: rgba(245,247,244,0.65); margin: 0 0 10px; }
.pvc-score__verdict { font-size: clamp(24px, 3.2vw, 34px); margin: 0 0 10px; line-height: 1.2; max-width: 24ch; color: #fff; }
.pvc-score__label { font-size: 14px; color: rgba(245,247,244,0.75); margin: 0; }

.pvc-bench { display: grid; grid-template-columns: repeat(3, 1fr); margin: 32px 0; border: 1px solid rgba(245,247,244,0.12); border-radius: 10px; overflow: hidden; }
@media (max-width: 720px) { .pvc-bench { grid-template-columns: 1fr; } }
.pvc-bench__col { padding: 22px 24px; border-right: 1px solid rgba(245,247,244,0.10); }
.pvc-bench__col:last-child { border-right: 0; }
@media (max-width: 720px) { .pvc-bench__col { border-right: 0; border-bottom: 1px solid rgba(245,247,244,0.10); } .pvc-bench__col:last-child { border-bottom: 0; } }
.pvc-bench__lab { display: block; font-size: 11px; letter-spacing: 0.12em; text-transform: uppercase; color: rgba(245,247,244,0.55); margin-bottom: 10px; }
.pvc-bench__val { font-family: 'Space Grotesk', sans-serif; font-weight: 600; font-size: 22px; color: #fff; }

.pvc-findings { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; margin: 0 0 36px; }
@media (max-width: 720px) { .pvc-findings { grid-template-columns: 1fr; } }
.pvc-findings__box { background: rgba(245,247,244,0.04); border-left: 3px solid; border-radius: 0 8px 8px 0; padding: 22px 24px; }
.pvc-findings__box--red   { border-left-color: #f87171; }
.pvc-findings__box--green { border-left-color: #4ade80; }
.pvc-findings__h { font-family: 'Space Grotesk', sans-serif; font-weight: 600; font-size: 13px; letter-spacing: 0.06em; text-transform: uppercase; margin: 0 0 14px; color: #fff; }
.pvc-findings__box ul { list-style: none; padding: 0; margin: 0; display: grid; gap: 12px; }
.pvc-findings__box li { font-size: 14px; color: rgba(245,247,244,0.82); line-height: 1.55; padding-left: 18px; position: relative; }
.pvc-findings__box--red li::before   { content: '!'; color: #f87171; font-weight: 700; position: absolute; left: 0; }
.pvc-findings__box--green li::before { content: '+'; color: #4ade80; font-weight: 700; position: absolute; left: 0; }

.pvc-lead { background: #f5f7f4; color: var(--pvc-ink); border-radius: 12px; padding: 36px; display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1.3fr); gap: 40px; align-items: start; }
@media (max-width: 920px) { .pvc-lead { grid-template-columns: 1fr; gap: 28px; padding: 28px; } }
.pvc-lead__copy h3 { font-size: 24px; margin: 0 0 14px; line-height: 1.2; color: var(--pvc-ink); }
.pvc-lead__copy p { color: var(--pvc-ink-2); font-size: 14px; margin: 0 0 18px; line-height: 1.6; }
.pvc-lead__bullets { list-style: none; padding: 0; margin: 0; display: grid; gap: 10px; }
.pvc-lead__bullets li { font-size: 13px; color: var(--pvc-ink-2); padding-left: 22px; position: relative; }
.pvc-lead__bullets li::before { content: ''; position: absolute; left: 0; top: 7px; width: 10px; height: 5px; border-left: 2px solid var(--pvc-accent); border-bottom: 2px solid var(--pvc-accent); transform: rotate(-45deg); }

.pvc-form { display: grid; gap: 14px; }
.pvc-form__row { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
@media (max-width: 520px) { .pvc-form__row { grid-template-columns: 1fr; } }
.pvc-form label { display: flex; flex-direction: column; font-size: 12px; color: var(--pvc-mute); }
.pvc-form label > span { font-family: 'Space Grotesk', sans-serif; font-weight: 500; font-size: 12px; letter-spacing: 0.04em; color: var(--pvc-ink); margin-bottom: 6px; }
.pvc-form label > span em { font-weight: 400; font-style: italic; color: var(--pvc-mute); }
.pvc-form input { font-family: 'Inter', sans-serif; font-size: 15px; padding: 12px 14px; border: 1px solid var(--pvc-line); background: #fff; color: var(--pvc-ink); border-radius: 8px; transition: border-color 180ms; }
.pvc-form input:focus { outline: none; border-color: var(--pvc-accent); box-shadow: 0 0 0 3px var(--pvc-accent-soft); }
.pvc-form__consent { display: grid; grid-template-columns: 20px 1fr; gap: 12px; font-size: 11px; color: var(--pvc-mute); line-height: 1.6; padding: 14px 0 0; }
.pvc-form__consent input { width: 18px; height: 18px; margin-top: 2px; accent-color: var(--pvc-accent); }
.pvc-form__send { display: flex; flex-direction: column; gap: 10px; align-items: flex-start; }

.pvc-thanks { background: #f5f7f4; color: var(--pvc-ink); border-radius: 12px; padding: 36px; text-align: center; }
.pvc-thanks h3 { margin: 0 0 12px; font-size: 24px; }
.pvc-thanks p { margin: 0; color: var(--pvc-ink-2); }

.pvc-byline { font-size: 12px; color: rgba(245,247,244,0.6); margin: 28px 0 0; text-align: center; line-height: 1.6; }
.pvc-byline strong { color: #f5f7f4; }

/* ── Method ──────────────────────────────────────────────────────────── */
.pvc-method { padding: 88px 0; }
.pvc-method__inner { max-width: 1200px; margin: 0 auto; padding: 0 28px; }
.pvc-method__grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 24px; margin-top: 36px; }
@media (max-width: 920px) { .pvc-method__grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 520px) { .pvc-method__grid { grid-template-columns: 1fr; } }
.pvc-method article { padding: 24px; background: var(--pvc-surface); border: 1px solid var(--pvc-line); border-radius: 12px; transition: border-color 180ms, transform 180ms; }
.pvc-method article:hover { border-color: var(--pvc-accent); transform: translateY(-2px); }
.pvc-method__no { display: inline-block; font-family: 'Space Grotesk', sans-serif; font-weight: 600; font-size: 12px; color: var(--pvc-accent); background: var(--pvc-accent-soft); padding: 4px 10px; border-radius: 4px; letter-spacing: 0.06em; margin-bottom: 14px; }
.pvc-method article h3 { font-size: 17px; line-height: 1.3; margin: 0 0 10px; }
.pvc-method article p { font-size: 13px; color: var(--pvc-ink-2); margin: 0; line-height: 1.6; }

/* ── FAQ ─────────────────────────────────────────────────────────────── */
.pvc-faq { padding: 88px 0; background: var(--pvc-surface); border-top: 1px solid var(--pvc-line); }
.pvc-faq__inner { max-width: 880px; margin: 0 auto; padding: 0 28px; }
.pvc-faq__list { display: grid; gap: 8px; margin-top: 36px; }
.pvc-faq__item { background: var(--pvc-page); border: 1px solid var(--pvc-line); border-radius: 10px; padding: 0; transition: border-color 180ms; }
.pvc-faq__item[open] { border-color: var(--pvc-accent); }
.pvc-faq__item summary { display: grid; grid-template-columns: 36px 1fr auto; gap: 14px; align-items: baseline; padding: 18px 22px; cursor: pointer; font-family: 'Space Grotesk', sans-serif; font-weight: 500; font-size: 16px; color: var(--pvc-ink); list-style: none; }
.pvc-faq__item summary::-webkit-details-marker { display: none; }
.pvc-faq__item summary::after { content: '+'; font-size: 22px; color: var(--pvc-accent); font-weight: 400; transition: transform 180ms; }
.pvc-faq__item[open] summary::after { content: '-'; }
.pvc-faq__no { font-size: 12px; color: var(--pvc-accent); letter-spacing: 0.08em; padding-top: 2px; }
.pvc-faq__item p { margin: 0; padding: 0 22px 20px 72px; font-size: 14px; color: var(--pvc-ink-2); line-height: 1.65; }
@media (max-width: 600px) { .pvc-faq__item p { padding-left: 22px; } }

/* ── Foot ────────────────────────────────────────────────────────────── */
.pvc-foot { background: var(--pvc-ink); color: rgba(245,247,244,0.6); padding: 28px 0; }
.pvc-foot__inner { max-width: 1200px; margin: 0 auto; padding: 0 28px; display: flex; justify-content: space-between; align-items: center; gap: 16px; font-size: 13px; flex-wrap: wrap; }
.pvc-foot a { color: rgba(245,247,244,0.7); text-decoration: none; margin-left: 22px; }
.pvc-foot a:hover { color: #fff; }

/* ── Sticky mobile CTA ───────────────────────────────────────────────── */
.pvc-sticky {
    display: none;
    position: fixed; left: 16px; right: 16px; bottom: 16px; z-index: 40;
    background: var(--pvc-accent); color: #fff;
    padding: 14px 18px; border-radius: 999px; text-decoration: none;
    font-family: 'Space Grotesk', sans-serif; font-weight: 600; font-size: 14px;
    box-shadow: 0 8px 24px rgba(21,128,61,0.35);
    align-items: center; justify-content: center; gap: 10px;
}
.pvc-sticky__dot { width: 8px; height: 8px; border-radius: 50%; background: #4ade80; box-shadow: 0 0 0 3px rgba(74,222,128,0.3); animation: pvc-pulse 1.6s ease-in-out infinite; }
.pvc-sticky__arr { font-size: 16px; }
@keyframes pvc-pulse { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.65; transform: scale(1.2); } }
@media (max-width: 700px) { .pvc-sticky { display: flex; } main { padding-bottom: 80px; } }

@media (prefers-reduced-motion: reduce) {
    .pvc *, .pvc *::before, .pvc *::after { transition: none !important; animation: none !important; }
}

/* ── Trust strip (images + cites + byline) — pvc-* unique treatment ── */
.pvc-trustbar {
    background: #f7f3ea;
    padding: 32px 18px;
    border-top: 1px solid rgba(20,17,13,0.08);
    border-bottom: 1px solid rgba(20,17,13,0.08);
}
.pvc-trustbar__inner {
    max-width: 1120px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: 1fr;
    gap: 22px;
    align-items: start;
}
@media (min-width: 820px) {
    .pvc-trustbar__inner {
        grid-template-columns: minmax(0, 0.95fr) minmax(0, 1.05fr);
        gap: 36px;
    }
}
.pvc-trustbar__photo {
    margin: 0;
    width: 100%;
    aspect-ratio: 4 / 3;
    border-radius: 12px;
    overflow: hidden;
    position: relative;
    background: #fff;
    border: 3px solid #15803D; box-shadow: 0 4px 14px -8px #15803D55;
}
.pvc-trustbar__photo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}
.pvc-trustbar__chip {
    position: absolute;
    top: 12px;
    left: 12px;
    background: rgba(255,255,255,0.94);
    color: #14110d;
    font-size: 11px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    font-weight: 600;
    padding: 6px 10px;
    border-radius: 999px;
    border: 1px solid rgba(20,17,13,0.08);
    backdrop-filter: blur(6px);
    margin: 0;
}
.pvc-trustbar__body {
    display: flex;
    flex-direction: column;
    gap: 16px;
}
.pvc-trustbar__lead {
    margin: 0;
    color: #3f3a32;
    font-size: 15px;
    line-height: 1.5;
    font-family: 'Inter', system-ui, sans-serif;
}
.pvc-trustbar__pills {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}
.pvc-trustbar__pill {
    display: inline-flex;
    align-items: center;
    border: 1px solid #15803D55;
    color: #14110d;
    background: #fff;
    border-radius: 999px;
    padding: 6px 12px;
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 13px;
    font-weight: 500;
    letter-spacing: 0.04em;
}
.pvc-trustbar__byline {
    display: flex;
    align-items: center;
    gap: 12px;
}
.pvc-trustbar__avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #15803D;
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-family: 'Inter', system-ui, sans-serif;
    font-weight: 700;
    font-size: 13px;
    letter-spacing: 0.02em;
    flex-shrink: 0;
}
.pvc-trustbar__bytxt {
    display: flex;
    flex-direction: column;
    line-height: 1.25;
}
.pvc-trustbar__byname {
    font-size: 13px;
    font-weight: 600;
    color: #14110d;
}
.pvc-trustbar__byrole {
    font-size: 12px;
    color: #3f3a32;
}
.pvc-trustbar__feature {
    margin: 8px 0 0;
    max-width: 360px;
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid rgba(20,17,13,0.10);
    box-shadow: 0 6px 22px -16px rgba(20,17,13,0.20);
    background: #fff;
}
.pvc-trustbar__feature img {
    width: 100%;
    height: auto;
    aspect-ratio: 16 / 10;
    object-fit: cover;
    display: block;
}
.pvc-trustbar__feature figcaption {
    font-size: 11px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #3f3a32;
    padding: 8px 12px;
    background: #fff;
}


</style>
